// src/components/EntityDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext, Link, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// No longer need to import queries since we're using modern API methods
import { formatDate, formatDateTime } from '../utils';
import CallEvalDisplay from './CallEvalDisplay'; // Adjust path if needed
import NarrativeSummary from './NarrativeSummary';
import { ROUTES, INTERNAL_CLIENT_ID } from '../constants/routes';
import { SingleDatePicker } from './FilterControls';
import ChangeHistoryPanel from './ChangeHistoryPanel';
import {
    GOAL_STATUS_OPTIONS,
    INITIATIVE_STATUS_OPTIONS,
    ACTION_ITEM_STATUS_OPTIONS,
    ISSUE_STATUS_OPTIONS,
    ISSUE_SEVERITY_OPTIONS,
    GENERIC_STATUS_OPTIONS
} from '../constants';
import { useStaffContext } from '../contexts/StaffContext';
import { RiDeleteBinLine } from 'react-icons/ri';

// --- Helper Components ---
const TrendChart = ({ data, dataKey, title, yAxisLabel, height = 200 }) => {
    if (!data || data.length === 0) return <div className="p-4 text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>No {title} data available.</div>;

    // --- Process data to ensure 'date' key exists and is formatted ---
    const formattedData = data.map(item => {
        // Identify the date key (wk from sentiment, weekBeginning from mentions)
        const dateValue = item.wk || item.weekBeginning;
        return {
            ...item,
            // Create a 'date' key with the formatted string for the XAxis
            date: dateValue ? formatDate(dateValue) : 'Unknown Date' // Use formatDate utility
        };
    }).filter(item => item.date !== 'Unknown Date'); // Filter out items where date couldn't be determined

    // Sort data by date just in case it's not already sorted
    formattedData.sort((a, b) => new Date(a.date) - new Date(b.date));

    return (
        <div 
            className="rounded-xl p-4 mt-4"
            style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
        >
            <h3 className="text-md font-semibold mb-3" style={{ color: 'var(--pallium-text-primary)' }}>{title}</h3>
            <ResponsiveContainer width="100%" height={height}>
                <LineChart data={formattedData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 10, offset: -5 }}/>
                    <Tooltip wrapperClassName="text-xs" />
                    <Line type="monotone" dataKey={dataKey} stroke="var(--pallium-primary-purple)" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

// Linked items displayed as cards based on entity type
const LinkedItemsCards = ({ items, title, itemType, linkPrefix, isLoading, error, clientId, navigate }) => {
    if (isLoading) return <div className="p-2 text-xs" style={{ color: 'var(--pallium-text-secondary)' }}>Loading {title}...</div>;
    if (error) return <div className="p-2 text-xs" style={{ color: 'var(--pallium-error-red)' }}>Error: {error}</div>;
    if (!items || items.length === 0) return <div className="p-2 text-xs" style={{ color: 'var(--pallium-icon-gray)' }}>No {title}.</div>;

    // Get icon and color based on item type
    const getTypeStyles = () => {
        switch (itemType) {
            case 'initiative':
                return { 
                    iconBg: 'rgba(0, 156, 220, 0.1)', 
                    iconColor: 'var(--pallium-info-cyan)',
                    hoverColor: 'var(--pallium-info-cyan)'
                };
            case 'issue':
                return { 
                    iconBg: 'rgba(252, 57, 57, 0.1)', 
                    iconColor: 'var(--pallium-error-red)',
                    hoverColor: 'var(--pallium-error-red)'
                };
            case 'goal':
                return { 
                    iconBg: 'rgba(110, 79, 196, 0.1)', 
                    iconColor: 'var(--pallium-primary-purple)',
                    hoverColor: 'var(--pallium-primary-purple)'
                };
            case 'action-item':
                return { 
                    iconBg: 'rgba(19, 185, 85, 0.1)', 
                    iconColor: 'var(--pallium-success-green)',
                    hoverColor: 'var(--pallium-success-green)'
                };
            default:
                return { 
                    iconBg: 'var(--pallium-purple-0-5)', 
                    iconColor: 'var(--pallium-primary-purple)',
                    hoverColor: 'var(--pallium-primary-purple)'
                };
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'open':
                return { bg: 'rgba(0, 156, 220, 0.1)', color: 'var(--pallium-info-cyan)' };
            case 'in progress':
            case 'in-progress':
                return { bg: 'rgba(110, 79, 196, 0.1)', color: 'var(--pallium-primary-purple)' };
            case 'on hold':
            case 'on-hold':
                return { bg: 'rgba(239, 163, 29, 0.1)', color: 'var(--pallium-warning-yellow)' };
            case 'completed':
            case 'closed':
            case 'resolved':
                return { bg: 'rgba(19, 185, 85, 0.1)', color: 'var(--pallium-success-green)' };
            default:
                return { bg: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-mute-text)' };
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'high':
            case 'critical':
                return { bg: 'rgba(252, 57, 57, 0.1)', color: 'var(--pallium-error-red)' };
            case 'medium':
                return { bg: 'rgba(239, 163, 29, 0.1)', color: 'var(--pallium-warning-yellow)' };
            case 'low':
                return { bg: 'rgba(19, 185, 85, 0.1)', color: 'var(--pallium-success-green)' };
            default:
                return { bg: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-mute-text)' };
        }
    };

    const typeStyles = getTypeStyles();

    // Render icon based on type
    const renderIcon = () => {
        switch (itemType) {
            case 'initiative':
                return (
                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                );
            case 'issue':
                return (
                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                );
            case 'goal':
                return (
                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                );
            case 'action-item':
                return (
                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                );
            default:
                return (
                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                    </svg>
                );
        }
    };

    return (
        <div className="mt-4">
            <h3 className="text-md font-semibold mb-3" style={{ color: 'var(--pallium-text-primary)' }}>{title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map(item => {
                    const handleClick = () => navigate(`${linkPrefix}/${item.id}`);
                    const statusStyle = getStatusColor(item.status);
                    const severityStyle = item.severity ? getSeverityColor(item.severity) : null;
                    
                    return (
                        <div 
                            key={item.id}
                            onClick={handleClick}
                            className="p-3 rounded-lg transition-all duration-200 cursor-pointer"
                            style={{
                                backgroundColor: 'var(--pallium-white)',
                                border: '1px solid var(--pallium-stroke-light)',
                                boxShadow: 'var(--pallium-shadow-sm)'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.boxShadow = 'var(--pallium-shadow-md)';
                                e.currentTarget.style.borderColor = typeStyles.hoverColor;
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.boxShadow = 'var(--pallium-shadow-sm)';
                                e.currentTarget.style.borderColor = 'var(--pallium-stroke-light)';
                            }}
                        >
                            <div className="flex items-start gap-2">
                                {/* Icon */}
                                <div 
                                    className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
                                    style={{ backgroundColor: typeStyles.iconBg, color: typeStyles.iconColor }}
                                >
                                    {renderIcon()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-sm leading-tight" style={{ color: 'var(--pallium-text-primary)' }}>
                                        {item.name}
                                    </h4>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        {item.status && (
                                            <span 
                                                className="px-2 py-0.5 rounded text-xs font-medium"
                                                style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}
                                            >
                                                {item.status}
                                            </span>
                                        )}
                                        {severityStyle && item.severity && (
                                            <span 
                                                className="px-2 py-0.5 rounded text-xs font-medium"
                                                style={{ backgroundColor: severityStyle.bg, color: severityStyle.color }}
                                            >
                                                {item.severity}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


const EntityDetailPage = ({ entityType: entityTypeFromProp }) => { // MODIFIED: Accept entityType as a prop
    const { entityId } = useParams(); // entityId is correctly from params
    const { apiService, clientId, departmentId } = useOutletContext();
    const navigate = useNavigate();
    const { getDriDisplayName } = useStaffContext();

    // Determine the effective client ID to use for API calls
    // If we have a real clientId, use it. Otherwise, if we're in a department context, use INTERNAL_CLIENT_ID
    const effectiveClientId = clientId || (departmentId ? INTERNAL_CLIENT_ID : null);

    const [entityData, setEntityData] = useState(null);
    const [mentionTrend, setMentionTrend] = useState([]);
    const [sentimentTrend, setSentimentTrend] = useState([]);
    const [linkedComms, setLinkedComms] = useState([]);
    const [linkedIssues, setLinkedIssues] = useState([]);
    const [linkedInitiatives, setLinkedInitiatives] = useState([]);

    const [loading, setLoading] = useState(true); // Initial state
    const [error, setError] = useState(null);

    const [loadingComms, setLoadingComms] = useState(true);
    const [loadingIssues, setLoadingIssues] = useState(true);
    const [loadingInitiatives, setLoadingInitiatives] = useState(true);

    const [isUpdating, setIsUpdating] = useState(false);
    const [updateError, setUpdateError] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    
    // Inline editing state for title and description
    const [editingField, setEditingField] = useState(null); // 'name' or 'description'
    const [editValue, setEditValue] = useState('');
    
    // DRI options for action items (loaded separately)
    const [driOptions, setDriOptions] = useState([]);

    // Load DRI options for all entity types that support DRI editing
    // Use all active staff as options, not just existing DRIs
    useEffect(() => {
        const loadDriOptions = async () => {
            if (!apiService) return;
            try {
                // First try to get all staff (more complete list for DRI selection)
                const staffResult = await apiService.getAllStaff(false); // Only active staff
                if (staffResult && staffResult.length > 0) {
                    // Map staff to DRI-compatible format (staff names)
                    const staffNames = staffResult.map(s => s.staffName || s.name).filter(Boolean);
                    setDriOptions(staffNames);
                } else if (effectiveClientId) {
                    // Fallback to client-specific DRIs
                    const result = await apiService.getClientDistinctDRIs(effectiveClientId);
                    setDriOptions(result.map(r => r.dri).filter(Boolean));
                }
            } catch (err) {
                console.error('Error loading DRI options:', err);
                // Try fallback
                if (effectiveClientId) {
                    try {
                        const result = await apiService.getClientDistinctDRIs(effectiveClientId);
                        setDriOptions(result.map(r => r.dri).filter(Boolean));
                    } catch (fallbackErr) {
                        console.error('Fallback DRI load also failed:', fallbackErr);
                    }
                }
            }
        };
        loadDriOptions();
    }, [apiService, effectiveClientId]);

    const fetchData = useCallback(async () => {
        // MODIFIED: Use entityTypeFromProp and effectiveClientId
        if (!apiService || !effectiveClientId || !entityTypeFromProp || !entityId) {
            setError("Missing necessary parameters to fetch data.");
            setLoading(false); // Ensure loading is stopped
            setLoadingComms(false);
            setLoadingIssues(false);
            setLoadingInitiatives(false);
            return;
        }

        // setLoading(true); // Set by useEffect calling this
        setError(null);
        // Ensure sub-loading states are reset at the beginning of a fetch attempt
        setLoadingComms(true);
        setLoadingIssues(true);
        setLoadingInitiatives(true);

        try {
            const promises = [];
            promises.push(apiService.getEntityDetails(entityTypeFromProp, effectiveClientId, entityId));
            promises.push(apiService.getEntityMentionTrend(effectiveClientId, entityId));
            promises.push(apiService.getEntitySentimentTrend(effectiveClientId, entityId));
            
            // Communications - common to all entity types
            promises.push(apiService.getEntityLinkedComms(effectiveClientId, entityId).finally(()=> setLoadingComms(false)));
            
            // Fetch related items based on entity type using specific API methods
            switch (entityTypeFromProp) {
                case 'goals':
                    promises.push(apiService.getGoalBlockingIssues(effectiveClientId, entityId).finally(()=> setLoadingIssues(false)));
                    promises.push(apiService.getGoalSupportingInitiatives(effectiveClientId, entityId).finally(()=> setLoadingInitiatives(false)));
                    break;
                case 'initiatives':
                    promises.push(apiService.getInitiativeBlockingIssues(effectiveClientId, entityId).finally(()=> setLoadingIssues(false)));
                    setLoadingInitiatives(false);
                    promises.push(Promise.resolve([])); // No initiatives linked to initiatives
                    break;
                case 'issues':
                    setLoadingIssues(false);
                    promises.push(Promise.resolve([])); // No issues linked to issues
                    promises.push(apiService.getIssueBlockedInitiatives(effectiveClientId, entityId).finally(()=> setLoadingInitiatives(false)));
                    break;
                case 'action-items':
                    setLoadingIssues(false);
                    promises.push(Promise.resolve([])); // No issues linked to action items
                    promises.push(apiService.getActionItemSupportedInitiative(effectiveClientId, entityId).finally(()=> setLoadingInitiatives(false)));
                    break;
                default:
                    throw new Error(`Unknown entity type: ${entityTypeFromProp}`);
            }

            const [detailsResult, mentionResult, sentimentResult, commsResult, issuesResult, initiativesResult] = await Promise.all(promises);

            setEntityData(detailsResult && detailsResult.length > 0 ? detailsResult[0] : null);
            setMentionTrend(mentionResult || []);
            setSentimentTrend(sentimentResult || []);
            setLinkedComms(commsResult || []);
            setLinkedIssues(issuesResult || []);
            setLinkedInitiatives(initiativesResult || []);

        } catch (err) {
            console.error(`Error fetching data for ${entityTypeFromProp} ${entityId}:`, err);
            setError(`Failed to load details: ${err.message}`);
            setEntityData(null); setMentionTrend([]); setSentimentTrend([]);
            setLinkedComms([]); setLinkedIssues([]); setLinkedInitiatives([]);
        } finally {
            setLoading(false); // Set main loading false
            // Sub-loading states are handled in their respective promise finally blocks
        }
    }, [apiService, effectiveClientId, entityTypeFromProp, entityId]); // MODIFIED: Use entityTypeFromProp and effectiveClientId

    useEffect(() => {
        setLoading(true); // Set loading to true before each fetch
        fetchData();
    }, [fetchData]);

    const handleFieldChange = async (fieldName, newValue) => {
        if (!entityData || !apiService || isUpdating) return;

        setIsUpdating(true);
        setUpdateError(null);
        const previousValue = entityData[fieldName];
        setEntityData(prev => ({ ...prev, [fieldName]: newValue }));

        try {
            if (fieldName === 'status') {
                await apiService.updateNodeStatus(entityId, newValue);
            } else if (fieldName === 'severity' && entityTypeFromProp === 'issues') {
                await apiService.updateIssueSeverity(entityId, newValue);
            } else if (fieldName === 'deadline' || fieldName === 'target_date') {
                // Use updateNodeProperty for deadline and target_date
                await apiService.updateNodeProperty(entityId, fieldName, newValue);
            } else if (fieldName === 'assigned_to' || fieldName === 'assignee') {
                await apiService.updateEntityDRI(entityId, newValue || null);
            } else if (fieldName === 'name' || fieldName === 'description') {
                // Use updateNodeProperty for name and description
                await apiService.updateNodeProperty(entityId, fieldName, newValue);
            } else {
                throw new Error(`Updating property "${fieldName}" is not supported for ${entityTypeFromProp}`);
            }
        } catch (err) {
            console.error(`Failed to update ${fieldName}:`, err);
            setUpdateError(`Failed to update ${fieldName}. Please try again.`);
            setEntityData(prev => ({ ...prev, [fieldName]: previousValue }));
        } finally {
            setIsUpdating(false);
            setEditingField(null);
        }
    };

    // Start inline editing for a field
    const startEditing = (fieldName) => {
        if (isUpdating) return;
        setEditingField(fieldName);
        setEditValue(entityData[fieldName] || '');
    };

    // Cancel inline editing
    const cancelEditing = () => {
        setEditingField(null);
        setEditValue('');
    };

    // Save inline edit
    const saveEdit = () => {
        if (editingField && editValue !== entityData[editingField]) {
            handleFieldChange(editingField, editValue);
        } else {
            cancelEditing();
        }
    };

    // Handle keyboard events for inline editing
    const handleEditKeyDown = (e) => {
        if (e.key === 'Enter' && editingField !== 'description') {
            e.preventDefault();
            saveEdit();
        } else if (e.key === 'Escape') {
            cancelEditing();
        }
    };

    const entityClientId =
        entityData?.client_id || entityData?.clientId || effectiveClientId;

    const handleDeleteEntity = async () => {
        if (!apiService || isDeleting) return;
        const supportedDeleteTypes = ['goals', 'initiatives', 'issues', 'action-items'];
        if (!supportedDeleteTypes.includes(entityTypeFromProp)) {
            setDeleteError(
                `Delete is not supported for entity type "${entityTypeFromProp}".`,
            );
            return;
        }
        setIsDeleting(true);
        setDeleteError(null);
        try {
            if (!entityClientId) {
                throw new Error('Client ID is required to delete this entity.');
            }
            if (entityTypeFromProp === 'goals') {
                await apiService.deleteGoal(entityClientId, entityId);
            } else if (entityTypeFromProp === 'initiatives') {
                await apiService.deleteInitiative(entityClientId, entityId);
            } else if (entityTypeFromProp === 'issues') {
                await apiService.deleteIssue(entityClientId, entityId);
            } else if (entityTypeFromProp === 'action-items') {
                await apiService.deleteActionItem(entityClientId, entityId);
            }
            navigate(backLink);
        } catch (err) {
            console.error('Failed to delete entity:', err);
            setDeleteError(err.message || 'Failed to delete entity.');
        } finally {
            setIsDeleting(false);
        }
    };

    const getStatusOptions = () => {
        // MODIFIED: Use entityTypeFromProp
        switch(entityTypeFromProp) {
            case 'goals': return GOAL_STATUS_OPTIONS;
            case 'initiatives': return INITIATIVE_STATUS_OPTIONS;
            case 'action-items': return ACTION_ITEM_STATUS_OPTIONS;
            case 'issues': return ISSUE_STATUS_OPTIONS;
            default:
                console.warn(`No specific status options for entity type: ${entityTypeFromProp}. Using generic list.`);
                return GENERIC_STATUS_OPTIONS;
        }
    };

    // --- Current options derived from the prop ---
    const currentStatusOptions = getStatusOptions();
    const currentSeverityOptions = entityTypeFromProp === 'issues' ? ISSUE_SEVERITY_OPTIONS : []; // MODIFIED

    // --- Render Logic ---
    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div><span className="ml-2 text-gray-700">Loading Details...</span></div>;
    }

    if (error) {
        return (
            <div 
                className="p-4 rounded-md"
                style={{ 
                    backgroundColor: 'var(--pallium-alert-error-bg)', 
                    borderLeft: '4px solid var(--pallium-error-red)', 
                    color: 'var(--pallium-error-red)' 
                }}
            >
                <p><strong>Error:</strong> {error}</p>
            </div>
        );
    }

    if (!entityData) {
         return (
             <div 
                 className="p-4 rounded-md"
                 style={{ 
                     backgroundColor: 'rgba(239, 163, 29, 0.1)', 
                     borderLeft: '4px solid var(--pallium-warning-yellow)', 
                     color: 'var(--pallium-warning-yellow)' 
                 }}
             >
                 <p><strong>Not Found:</strong> Could not load data for {entityTypeFromProp} with ID {entityId}.</p>
             </div>
         );
    }

    // Determine link prefixes based on entity type (adjust as needed)
    // Determine link prefixes based on context (department or client)
    const issueLinkPrefix = departmentId && !clientId 
        ? `${ROUTES.OPERATIONS}/${departmentId}/issues`
        : `${ROUTES.ACCOUNTS}/${effectiveClientId}/issues`;
    const initiativeLinkPrefix = departmentId && !clientId
        ? `${ROUTES.OPERATIONS}/${departmentId}/initiatives`
        : `${ROUTES.ACCOUNTS}/${effectiveClientId}/initiatives`;

    // Determine the back link based on context
    const backLink = departmentId && !clientId
        ? `${ROUTES.OPERATIONS}/${departmentId}/${entityTypeFromProp}`
        : `${ROUTES.ACCOUNTS}/${effectiveClientId}/${entityTypeFromProp}`;


    // Ensure the link back uses entityTypeFromProp
    const entityTypeDisplay = entityTypeFromProp ? entityTypeFromProp.charAt(0).toUpperCase() + entityTypeFromProp.slice(1) : 'List';

    // Get icon and color for entity type
    const getEntityTypeStyles = () => {
        switch (entityTypeFromProp) {
            case 'goals':
                return { 
                    bgColor: 'var(--pallium-purple-1)', 
                    iconColor: 'var(--pallium-primary-purple)',
                    icon: <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                };
            case 'initiatives':
                return { 
                    bgColor: 'rgba(0, 156, 220, 0.15)', 
                    iconColor: 'var(--pallium-info-cyan)',
                    icon: <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                };
            case 'action-items':
                return { 
                    bgColor: 'rgba(19, 185, 85, 0.15)', 
                    iconColor: 'var(--pallium-success-green)',
                    icon: <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                };
            case 'issues':
                return { 
                    bgColor: 'rgba(252, 57, 57, 0.15)', 
                    iconColor: 'var(--pallium-error-red)',
                    icon: <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                };
            default:
                return { 
                    bgColor: 'var(--pallium-purple-1)', 
                    iconColor: 'var(--pallium-primary-purple)',
                    icon: <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                };
        }
    };
    
    const entityStyles = getEntityTypeStyles();

    return (
        <div>
            <Link 
                to={backLink} 
                className="text-sm hover:underline mb-4 inline-block"
                style={{ color: 'var(--pallium-link-default)' }}
            >
                &larr; Back to {entityTypeDisplay}
            </Link>

            {isUpdating && (
                <div 
                    className="mb-4 p-2 text-sm rounded-md"
                    style={{ backgroundColor: 'var(--pallium-purple-0-5)', color: 'var(--pallium-primary-purple)' }}
                >
                    Updating...
                </div>
            )}
            {updateError && (
                <div 
                    className="mb-4 p-2 text-sm rounded-md"
                    style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}
                >
                    Error: {updateError}
                </div>
            )}

            {entityData && (
                <div 
                    className="rounded-xl p-6 mb-6"
                    style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                >
                    <div className="flex items-start gap-4">
                        {/* Entity type icon */}
                        <div 
                            className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: entityStyles.bgColor }}
                        >
                            <svg 
                                className="w-8 h-8"
                                style={{ color: entityStyles.iconColor }}
                                xmlns="http://www.w3.org/2000/svg" 
                                viewBox="0 0 20 20" 
                                fill="currentColor"
                            >
                                {entityStyles.icon}
                            </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                    {/* Inline editable title */}
                                    {editingField === 'name' ? (
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onBlur={saveEdit}
                                            onKeyDown={handleEditKeyDown}
                                            autoFocus
                                            className="text-2xl font-bold mb-2 w-full px-2 py-1 rounded-lg focus:outline-none focus:ring-2"
                                            style={{ 
                                                color: 'var(--pallium-text-primary)',
                                                border: '1px solid var(--pallium-primary-purple)',
                                                backgroundColor: 'var(--pallium-white)'
                                            }}
                                            disabled={isUpdating}
                                        />
                                    ) : (
                                        <h1 
                                            className="text-2xl font-bold mb-2 cursor-pointer hover:bg-gray-100 px-2 py-1 -mx-2 rounded-lg transition-colors"
                                            style={{ color: 'var(--pallium-text-primary)' }}
                                            onClick={() => startEditing('name')}
                                            title="Click to edit"
                                        >
                                            {entityData.name || 'No Name'}
                                        </h1>
                                    )}
                                    {/* Inline editable description */}
                                    {editingField === 'description' ? (
                                        <div className="mb-3">
                                            <textarea
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Escape') cancelEditing();
                                                }}
                                                autoFocus
                                                rows={3}
                                                className="w-full px-2 py-1 rounded-lg focus:outline-none focus:ring-2 resize-none"
                                                style={{ 
                                                    color: 'var(--pallium-text-secondary)',
                                                    border: '1px solid var(--pallium-primary-purple)',
                                                    backgroundColor: 'var(--pallium-white)'
                                                }}
                                                disabled={isUpdating}
                                            />
                                            <div className="flex gap-2 mt-1">
                                                <button
                                                    onClick={saveEdit}
                                                    className="px-2 py-1 text-xs font-medium rounded"
                                                    style={{ backgroundColor: 'var(--pallium-primary-purple)', color: 'white' }}
                                                    disabled={isUpdating}
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={cancelEditing}
                                                    className="px-2 py-1 text-xs font-medium rounded"
                                                    style={{ backgroundColor: 'var(--pallium-bg-secondary)', color: 'var(--pallium-text-secondary)' }}
                                                    disabled={isUpdating}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <p 
                                            style={{ color: 'var(--pallium-text-secondary)' }} 
                                            className="mb-3 cursor-pointer hover:bg-gray-100 px-2 py-1 -mx-2 rounded-lg transition-colors"
                                            onClick={() => startEditing('description')}
                                            title="Click to edit"
                                        >
                                            {entityData.description || <span className="italic">No description provided.</span>}
                                        </p>
                                    )}
                                </div>
                                <div className="flex flex-row gap-2">
                                    <button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg"
                                        style={{ backgroundColor: 'rgba(252, 57, 57, 0.1)', color: 'var(--pallium-error-red)' }}
                                    >
                                        <RiDeleteBinLine className="w-4 h-4" />
                                        Delete
                                    </button>
                                </div>
                            </div>

                    <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm items-center mt-4">
                         {entityData.hasOwnProperty('status') && (
                             <div className="flex items-center space-x-2">
                                 <strong style={{ color: 'var(--pallium-text-tertiary)' }}>Status:</strong>
                                 <select
                                     value={entityData.status || ''}
                                     onChange={(e) => handleFieldChange('status', e.target.value)}
                                     disabled={isUpdating}
                                     className="p-1 text-sm focus:outline-none"
                                     style={{
                                         border: '1px solid var(--pallium-border-default)',
                                         borderRadius: 'var(--pallium-border-radius)',
                                         backgroundColor: isUpdating ? 'var(--pallium-bg-secondary)' : 'var(--pallium-white)',
                                         cursor: isUpdating ? 'not-allowed' : 'pointer'
                                     }}
                                 >
                                     {entityData.status && !currentStatusOptions.includes(entityData.status) && (
                                        <option value={entityData.status} disabled>{String(entityData.status).charAt(0).toUpperCase() + String(entityData.status).slice(1)} (Current)</option>
                                     )}
                                     {currentStatusOptions.map(option => (
                                         <option key={option} value={option}>
                                             {option.charAt(0).toUpperCase() + option.slice(1)}
                                         </option>
                                     ))}
                                 </select>
                             </div>
                         )}
                         {entityTypeFromProp === 'issues' && entityData.hasOwnProperty('severity') && (
                              <div className="flex items-center space-x-2">
                                 <strong style={{ color: 'var(--pallium-text-tertiary)' }}>Severity:</strong>
                                 <select
                                     value={entityData.severity || ''}
                                     onChange={(e) => handleFieldChange('severity', e.target.value)}
                                     disabled={isUpdating}
                                     className="p-1 text-sm focus:outline-none"
                                     style={{
                                         border: '1px solid var(--pallium-border-default)',
                                         borderRadius: 'var(--pallium-border-radius)',
                                         backgroundColor: isUpdating ? 'var(--pallium-bg-secondary)' : 'var(--pallium-white)',
                                         cursor: isUpdating ? 'not-allowed' : 'pointer'
                                     }}
                                 >
                                     {entityData.severity && !currentSeverityOptions.includes(entityData.severity) && (
                                        <option value={entityData.severity} disabled>{entityData.severity.charAt(0).toUpperCase() + entityData.severity.slice(1)} (Current)</option>
                                     )}
                                     {currentSeverityOptions.map(option => (
                                         <option key={option} value={option}>
                                             {option.charAt(0).toUpperCase() + option.slice(1)}
                                         </option>
                                     ))}
                                 </select>
                              </div>
                         )}
                         {entityTypeFromProp === 'action-items' && entityData.hasOwnProperty('deadline') && (
                             <div className="flex items-center space-x-2">
                                 <strong style={{ color: 'var(--pallium-text-tertiary)' }}>Deadline:</strong>
                                 <SingleDatePicker
                                     value={entityData.deadline ? formatDate(entityData.deadline) : ''}
                                     onChange={(newValue) => handleFieldChange('deadline', newValue)}
                                     disabled={isUpdating}
                                 />
                             </div>
                         )}
                         {entityTypeFromProp !== 'action-items' && entityData.deadline && <span><strong>Deadline:</strong> {formatDate(entityData.deadline)}</span>}
                         {/* Editable Assignee for action items only (dropdown) */}
                         {entityTypeFromProp === 'action-items' && (
                             <div className="flex items-center space-x-2">
                                 <strong style={{ color: 'var(--pallium-text-tertiary)' }}>Assignee:</strong>
                                 <select
                                     value={entityData.assigned_to || entityData.assignee || ''}
                                     onChange={(e) => handleFieldChange('assigned_to', e.target.value)}
                                     disabled={isUpdating}
                                     className="p-1 text-sm focus:outline-none"
                                     style={{
                                         border: '1px solid var(--pallium-border-default)',
                                         borderRadius: 'var(--pallium-border-radius)',
                                         backgroundColor: isUpdating ? 'var(--pallium-bg-secondary)' : 'var(--pallium-white)',
                                         cursor: isUpdating ? 'not-allowed' : 'pointer'
                                     }}
                                 >
                                    <option value="">Unassigned</option>
                                    {/* Show current value if not in options */}
                                    {(entityData.assigned_to || entityData.assignee) && 
                                     !driOptions.includes(entityData.assigned_to || entityData.assignee) && (
                                        <option value={entityData.assigned_to || entityData.assignee}>
                                            {getDriDisplayName(entityData.assigned_to || entityData.assignee)} (Current)
                                        </option>
                                    )}
                                    {driOptions.map(dri => (
                                        <option key={dri} value={dri}>{getDriDisplayName(dri)}</option>
                                    ))}
                                 </select>
                             </div>
                         )}
                         {/* Editable DRI for goals, initiatives, issues */}
                         {['goals', 'initiatives', 'issues'].includes(entityTypeFromProp) && (
                             <div className="flex items-center space-x-2">
                                 <strong style={{ color: 'var(--pallium-text-tertiary)' }}>DRI:</strong>
                                <select
                                    value={entityData.assigned_to ?? entityData.assignee ?? entityData.dri ?? ''}
                                    onChange={(e) => handleFieldChange('assigned_to', e.target.value)}
                                     disabled={isUpdating}
                                     className="p-1 text-sm focus:outline-none"
                                     style={{
                                         border: '1px solid var(--pallium-border-default)',
                                         borderRadius: 'var(--pallium-border-radius)',
                                         backgroundColor: isUpdating ? 'var(--pallium-bg-secondary)' : 'var(--pallium-white)',
                                         cursor: isUpdating ? 'not-allowed' : 'pointer'
                                     }}
                                 >
                                    <option value="">Auto-calculate DRI</option>
                                    {/* Show current value if not in options */}
                                    {(entityData.assigned_to || entityData.assignee || entityData.dri) && 
                                     !driOptions.includes(entityData.assigned_to || entityData.assignee || entityData.dri) && (
                                        <option value={entityData.assigned_to || entityData.assignee || entityData.dri}>
                                            {getDriDisplayName(entityData.assigned_to || entityData.assignee || entityData.dri)} (Current)
                                        </option>
                                    )}
                                    {driOptions.map(dri => (
                                        <option key={dri} value={dri}>{getDriDisplayName(dri)}</option>
                                    ))}
                                 </select>
                                 {!(entityData.assigned_to || entityData.assignee) && entityData.dri && (
                                     <span className="text-xs italic" style={{ color: 'var(--pallium-icon-gray)' }}>(calculated)</span>
                                 )}
                             </div>
                         )}
                         {/* Editable Target Date for goals and initiatives */}
                         {['goals', 'initiatives'].includes(entityTypeFromProp) && (
                             <div className="flex items-center space-x-2">
                                 <strong style={{ color: 'var(--pallium-text-tertiary)' }}>Target Date:</strong>
                                 <SingleDatePicker
                                     value={entityData.targetDate || entityData.target_date ? formatDate(entityData.targetDate || entityData.target_date) : ''}
                                     onChange={(newValue) => handleFieldChange('target_date', newValue)}
                                     disabled={isUpdating}
                                 />
                             </div>
                         )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {entityData && (
                <NarrativeSummary
                    whatsHappening={entityData.narrativeWhatsHappening}
                    whyItMatters={entityData.narrativeWhyItMatters}
                    whatToDo={entityData.narrativeWhatToDo}
                    updatedAt={entityData.narrativeUpdatedAt}
                    defaultExpanded={true}
                    title="AI Briefing"
                />
            )}

            {/* Trends Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <TrendChart data={mentionTrend} dataKey="mentions" title="Mention Trend (Weekly)" yAxisLabel="Mentions" />
                <TrendChart data={sentimentTrend} dataKey="avgSentiment" title="Sentiment Trend (Weekly)" yAxisLabel="Avg. Sentiment" />
            </div>

            {/* Linked Items Section */}
            <div 
                className="rounded-xl p-6"
                style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
            >
                 <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--pallium-text-primary)' }}>Linked Items</h2>

                 {/* Render linked items based on entity type */}
                 {/* Example for a Goal */}
                 {entityTypeFromProp === 'goals' && (
                     <>
                        <LinkedItemsCards 
                            items={linkedInitiatives} 
                            title="Supporting Initiatives" 
                            itemType="initiative"
                            linkPrefix={initiativeLinkPrefix} 
                            isLoading={loadingInitiatives} 
                            clientId={clientId}
                            navigate={navigate}
                        />
                        <LinkedItemsCards 
                            items={linkedIssues} 
                            title="Blocking Issues (via Initiatives)" 
                            itemType="issue"
                            linkPrefix={issueLinkPrefix} 
                            isLoading={loadingIssues} 
                            clientId={clientId}
                            navigate={navigate}
                        />
                     </>
                 )}

                 {/* Example for an Initiative */}
                  {entityTypeFromProp === 'initiatives' && (
                     <>
                         <LinkedItemsCards 
                             items={linkedIssues} 
                             title="Blocking Issues" 
                             itemType="issue"
                             linkPrefix={issueLinkPrefix} 
                             isLoading={loadingIssues} 
                             clientId={clientId}
                             navigate={navigate}
                         />
                     </>
                 )}

                  {/* Example for an Issue */}
                  {entityTypeFromProp === 'issues' && (
                     <>
                         <LinkedItemsCards 
                             items={linkedInitiatives} 
                             title="Blocked Initiatives" 
                             itemType="initiative"
                             linkPrefix={initiativeLinkPrefix} 
                             isLoading={loadingInitiatives} 
                             clientId={clientId}
                             navigate={navigate}
                         />
                     </>
                 )}

                 {/* Example for an Action Item */}
                 {entityTypeFromProp === 'action-items' && (
                      <>
                          <LinkedItemsCards 
                              items={linkedInitiatives} 
                              title="Supporting Initiative" 
                              itemType="initiative"
                              linkPrefix={initiativeLinkPrefix} 
                              isLoading={loadingInitiatives} 
                              clientId={clientId}
                              navigate={navigate}
                          />
                      </>
                  )}

            </div>
            {/* Column 2: Related Communications */}
            <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--pallium-text-primary)' }}>Related Communications</h3>
                {loadingComms && <div className="p-2 text-xs text-gray-500">Loading communications...</div>}
                {!loadingComms && linkedComms.length === 0 && <div className="p-2 text-xs text-gray-400">No related communications found.</div>}
                {!loadingComms && linkedComms.length > 0 && (
                    <div className="space-y-5">
                        {linkedComms.map(comm => (
                            <div
                                key={comm.id}
                                className="rounded-xl p-4"
                                style={{
                                    backgroundColor: 'var(--pallium-white)',
                                    border: '1px solid var(--pallium-stroke-light)',
                                    boxShadow: 'var(--pallium-shadow-sm)',
                                }}
                            >
                                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2 mb-2">
                                    <div>
                                        <span className="text-sm font-semibold leading-snug" style={{ color: 'var(--pallium-text-primary)' }}>{comm.title || `(${comm.type || 'Communication'})`}</span>
                                        <div className="text-xs mt-1" style={{ color: 'var(--pallium-text-tertiary)' }}>
                                            {comm.staff && <span>Staff: {comm.staff}</span>}
                                            {comm.type && <span className="ml-2 italic">({comm.type})</span>}
                                        </div>
                                    </div>
                                    <span className="text-xs whitespace-nowrap" style={{ color: 'var(--pallium-text-tertiary)' }}>{formatDateTime(comm.date)}</span>
                                </div>
                                {comm.summary && (
                                    <p className="text-sm leading-relaxed mb-3 whitespace-pre-line" style={{ color: 'var(--pallium-text-secondary)' }}>{comm.summary}</p>
                                )}
                                {/* --- Use CallEvalDisplay component --- */}
                                <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--pallium-stroke-light)' }}>
                                    <CallEvalDisplay evaluations={comm.callEvaluations} />
                                </div>
                                {/* --- End Call Evaluations Display --- */}
                                {/* Display Snippets */}
                                {Array.isArray(comm.snippets) && comm.snippets.length > 0 && (
                                    <div className="mt-3">
                                        <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--pallium-text-tertiary)' }}>Relevant Snippets</h4>
                                        <div className="space-y-2">
                                            {comm.snippets.map((snippet, index) => (
                                                    snippet && ( // Render only if snippet is not null/empty
                                                    <blockquote
                                                        key={index}
                                                        className="text-xs leading-relaxed rounded-lg p-3"
                                                        style={{
                                                            backgroundColor: 'rgba(0, 156, 220, 0.06)',
                                                            borderLeft: '3px solid var(--pallium-info-cyan)',
                                                            color: 'var(--pallium-text-secondary)'
                                                        }}
                                                    >
                                                        {snippet}
                                                    </blockquote>
                                                    )
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div> {/* End Communications Column */}

            {/* Change History */}
            {apiService && entityId && (
                <ChangeHistoryPanel
                    nodeId={entityId}
                    apiService={apiService}
                    onRevert={fetchData}
                />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div
                        className="rounded-xl p-6 w-full max-w-md"
                        style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-lg)' }}
                    >
                        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--pallium-text-primary)' }}>
                            Delete {entityData?.name || 'Entity'}?
                        </h2>
                        <p className="text-sm mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                            This action cannot be undone.
                        </p>
                        {deleteError && (
                            <div
                                className="mb-4 p-3 rounded-lg text-sm"
                                style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}
                            >
                                {deleteError}
                            </div>
                        )}
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 text-sm font-medium rounded-lg"
                                style={{ color: 'var(--pallium-text-secondary)', backgroundColor: 'var(--pallium-bg-secondary)' }}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteEntity}
                                className="px-4 py-2 text-sm font-medium rounded-lg text-white"
                                style={{ backgroundColor: isDeleting ? 'var(--pallium-icon-gray)' : 'var(--pallium-error-red)' }}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default EntityDetailPage;